// Lazy Shiki syntax-highlighter singleton — loads each language the first time it is used.
//
// Languages load on demand, not all at creation: creating the highlighter with
// every language in EXTENSION_MAP took ~350 ms of main-thread script on a
// desktop (~1.4 s on a phone, MA-461), against ~35 ms for the themes alone plus
// a few ms per language. https://shiki.style/guide/best-performance
import type { BundledLanguage, BundledTheme, HighlighterGeneric } from 'shiki';

type Highlighter = HighlighterGeneric<BundledLanguage, BundledTheme>;

let highlighterPromise: Promise<Highlighter> | null = null;
/** One load per language, shared by concurrent callers. */
const languageLoads = new Map<BundledLanguage, Promise<void>>();

const EXTENSION_MAP: Record<string, BundledLanguage> = {
  ts: 'typescript',
  tsx: 'tsx',
  js: 'javascript',
  jsx: 'jsx',
  json: 'json',
  md: 'markdown',
  mdx: 'mdx',
  css: 'css',
  scss: 'scss',
  html: 'html',
  xml: 'xml',
  svg: 'xml',
  py: 'python',
  go: 'go',
  rs: 'rust',
  yaml: 'yaml',
  yml: 'yaml',
  toml: 'toml',
  sql: 'sql',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  dockerfile: 'dockerfile',
  docker: 'dockerfile',
  graphql: 'graphql',
  gql: 'graphql',
  c: 'c',
  cpp: 'cpp',
  h: 'c',
  hpp: 'cpp',
  java: 'java',
  rb: 'ruby',
  php: 'php',
  lua: 'lua',
  swift: 'swift',
  kt: 'kotlin',
  vue: 'vue',
  svelte: 'svelte',
  prisma: 'prisma',
  env: 'dotenv',
};

export function detectLanguage(filePath: string): BundledLanguage | null {
  const name = filePath.split('/').pop() ?? '';
  const lower = name.toLowerCase();

  if (lower === 'dockerfile' || lower.startsWith('dockerfile.')) return 'dockerfile';
  if (lower === 'makefile' || lower === 'gnumakefile') return 'makefile';
  if (lower === '.gitignore' || lower === '.dockerignore') return 'bash';

  const ext = lower.includes('.') ? (lower.split('.').pop() ?? '') : '';
  return EXTENSION_MAP[ext] ?? null;
}

// A failed load is forgotten so the next call retries — over the relay, a
// chunk fetch can fail once and succeed later.
function getHighlighter(): Promise<Highlighter> {
  if (!highlighterPromise) {
    const created = import('shiki').then(({ createHighlighter }) =>
      createHighlighter({ themes: ['github-dark', 'github-light'], langs: [] }),
    );
    created.catch(() => {
      if (highlighterPromise === created) highlighterPromise = null;
    });
    highlighterPromise = created;
  }
  return highlighterPromise;
}

function loadLanguage(highlighter: Highlighter, lang: BundledLanguage): Promise<void> {
  let load = languageLoads.get(lang);
  if (!load) {
    load = highlighter.loadLanguage(lang);
    const started = load;
    started.catch(() => {
      if (languageLoads.get(lang) === started) languageLoads.delete(lang);
    });
    languageLoads.set(lang, load);
  }
  return load;
}

// Emit dual-theme output: `color`/`background-color` carry the light theme,
// and CSS variables `--shiki-dark` / `--shiki-dark-bg` carry the dark theme.
// `global.css` switches between them based on the `.dark` class on <html>.
export async function highlightCode(code: string, lang: BundledLanguage): Promise<string> {
  const highlighter = await getHighlighter();
  await loadLanguage(highlighter, lang);
  return highlighter.codeToHtml(code, {
    lang,
    themes: { light: 'github-light', dark: 'github-dark' },
    defaultColor: 'light',
  });
}
